import { Injectable } from '@angular/core';

import { RestService } from './general/rest.service';
import { AuthService } from './auth/auth.service';
import { ChatMessageModel } from './notifications/chat.models';
import { GetUnlessTokenGQL } from './adapters/unless.generated';
import { UID } from '@saliente/library';
import { gql } from 'apollo-angular';

const SNIPPET_IDS: { [chatTypeEid: string]: string } = {
    'FIN': '8dadcadb-1327-4651-91e2-3fbaf235243f',   // Connect Contabilitate
    'PAY': '8dadcadb-1327-4651-91e2-3fbaf235243f',   // Connect Salarizare — populate when bot is created
    'INV': '8dadcadb-1327-4651-91e2-3fbaf235243f',   // Connect Facturare — populate when bot is created
    'TECH': '8dadcadb-1327-4651-91e2-3fbaf235243f',  // Admin — populate when bot is created
};

const CLIENT_CONTEXT_QUERY = gql`
    query getClientContextBlock($clientEid: String!, $channelCode: String!) {
        notifications {
            getClientContextBlockUsingGET(clientEid: $clientEid, channelCode: $channelCode) {
                pairs {
                    key
                    value
                }
            }
        }
    }
`;

@Injectable()
export class UnlessChatService {

    private sdkReady = false;
    private installerScript: HTMLScriptElement | null = null;
    private currentSnippetId: string | null = null;
    private txtLoadedHandler: (() => void) | null = null;
    private currentClientEid: string | null = null;
    private currentChatTypeEid: string | null = null;
    private cachedContextClientEid: string | null = null;
    private cachedContextData: { [key: string]: string } | null = null;

    constructor(
        private restService: RestService,
        private authService: AuthService
    ) {}

    getSnippetIdForChatType(chatTypeEid: string): string {
        return SNIPPET_IDS[chatTypeEid] || '';
    }

    init(snippetId: string, clientEid: string, chatTypeEid: string): void {
        if (!snippetId) {
            return;
        }

        // Same bot (already loaded, or still loading): just track which client is in focus.
        // A different client invalidates any context already pushed to Unless (re-pushed lazily
        // on that client's first question). This is the fix for switching clients within one channel.
        if (this.currentSnippetId === snippetId) {
            if (this.currentClientEid !== clientEid) {
                this.cachedContextClientEid = null;
                this.cachedContextData = null;
            }
            this.currentClientEid = clientEid;
            this.currentChatTypeEid = chatTypeEid;
            return;
        }

        // A different bot is loaded: tear it down first.
        if (this.currentSnippetId) {
            this.destroy();
        }

        this.cachedContextClientEid = null;
        this.cachedContextData = null;
        this.currentClientEid = clientEid;
        this.currentChatTypeEid = chatTypeEid;
        this.currentSnippetId = snippetId;

        // Listen for SDK ready event, then load the Unless v5 loader IIFE.
        // NOTE: the client context is loaded lazily — only when the first question is sent
        // (see sendToWizard), not here — to avoid assembling context for clients never asked about.
        this.txtLoadedHandler = () => this.onSdkReady();
        document.addEventListener('txt-loaded', this.txtLoadedHandler);

        this.loadUnlessScript(snippetId);
    }

    destroy(): void {
        try {
            if (this.sdkReady && typeof Txt !== 'undefined' && Txt.isChatOpen()) {
                Txt.closeChat();
            }
        } catch (e) {
            // Ignore errors during cleanup
        }

        if (this.txtLoadedHandler) {
            document.removeEventListener('txt-loaded', this.txtLoadedHandler);
            this.txtLoadedHandler = null;
        }

        // Remove the installer script we created
        if (this.installerScript && this.installerScript.parentNode) {
            this.installerScript.parentNode.removeChild(this.installerScript);
            this.installerScript = null;
        }

        // Remove the SDK script and preload link created by the IIFE
        document.querySelectorAll('script[src*=".unless.com"]').forEach(el => el.remove());
        document.querySelectorAll('link[href*=".unless.com"]').forEach(el => el.remove());

        delete (window as any).TxtOptions;
        this.sdkReady = false;
        this.currentSnippetId = null;
        this.currentClientEid = null;
        this.currentChatTypeEid = null;
        this.cachedContextClientEid = null;
        this.cachedContextData = null;
    }

    sendToWizard(messages: ChatMessageModel[]): void {
        if (!this.sdkReady) {
            return;
        }

        const sorted = messages.slice().sort((a, b) => {
            const dateA = a.datePosted ? new Date(a.datePosted).getTime() : 0;
            const dateB = b.datePosted ? new Date(b.datePosted).getTime() : 0;
            return dateA - dateB;
        });

        const concatenated = sorted.map(m => {
            const role = m.authorIsExpert || m.authorIsKeez ? '[Expert]' : '[Client]';
            return `${role}: ${m.content}`;
        }).join('\n');

		let uid = "Asistentul Connect (" + UID() + ")";

        const openChat = () => Txt.openChat({ conversationName: uid, prompt: concatenated });

        // Load the client context as late as possible: only now, when the first question is about to be
        // sent to Unless. It's fetched once per client and re-applied as AI procedure data before every
        // question, so each conversation carries it. Fails open — openChat runs regardless.
        this.applyClientContext(this.currentClientEid, this.currentChatTypeEid, openChat);
    }

    private loadUnlessScript(snippetId: string): void {
        if (!/^[a-zA-Z0-9\-]+$/.test(snippetId)) {
            console.error('UnlessChatService: invalid snippet ID');
            return;
        }
        // Inject the exact Unless v5 snippet IIFE as an inline script
        this.installerScript = document.createElement('script');
        this.installerScript.setAttribute('data-unless', '1.3');
        this.installerScript.setAttribute('data-installer', 'snippet');
        this.installerScript.textContent =
            '!function(e,n,t,o,d,c,i){function a(e){o.parentNode.insertBefore(e,o)}' +
            'e&&(o=(e.head||e.documentElement).firstChild,' +
            'i="https://"+t+".unless.com/js/v5/latest/txt.min.js?id="+t+"&domain="+window.location.hostname,' +
            '(d=e.createElement("link")).href=i,d.rel="preconnect preload",d.as=n,a(d),' +
            '(c=e.createElement(n)).src=i,a(c))}' +
            '("undefined"!=typeof document?document:null,"script","' + snippetId + '");';
        document.head.appendChild(this.installerScript);
    }

    private onSdkReady(): void {
        this.sdkReady = true;
        this.fetchJwtAndInitialize();
        // Client context is pushed lazily on the first question (sendToWizard), not on load.
    }

    /**
     * Supplies the assembled client context to the Unless AI as PROCEDURE DATA (Txt.setProcedureData) —
     * the documented channel the AI procedure consumes; it never appears in the conversation. Called right
     * before a question is sent. The key/value pairs are fetched once per client (cached) and re-applied
     * before every question. Fails open: on error it still calls `onDone`, so the question is never blocked.
     */
    private applyClientContext(clientEid: string | null, chatTypeEid: string | null, onDone: () => void): void {
        if (!this.sdkReady || !clientEid || !chatTypeEid || typeof Txt === 'undefined') {
            onDone();
            return;
        }

        // Reuse the data already fetched for this client — just (re)apply it as procedure data.
        if (this.cachedContextClientEid === clientEid && this.cachedContextData) {
            this.setProcedureData(this.cachedContextData);
            onDone();
            return;
        }

        this.restService.query({
            query: CLIENT_CONTEXT_QUERY,
            variables: { clientEid: clientEid, channelCode: chatTypeEid },
        } as any, { spinner: false, error: false }).subscribe(
            (response: any) => {
                const pairs = response?.data?.notifications?.getClientContextBlockUsingGET?.pairs;
                if (pairs && pairs.length) {
                    const data: { [key: string]: string } = {};
                    pairs.forEach((p: any) => { if (p && p.key) { data[p.key] = p.value; } });
                    this.cachedContextClientEid = clientEid;
                    this.cachedContextData = data;
                    this.setProcedureData(data);
                }
                onDone();
            },
            () => {
                // Fail open — never block sending the question because context could not be assembled.
                onDone();
            }
        );
    }

    private setProcedureData(data: { [key: string]: string }): void {
        if (typeof Txt !== 'undefined' && data && Object.keys(data).length) {
            // Unless AI procedure data (string key/value pairs). The bot's procedure consumes these keys.
            Txt.setProcedureData(data);
        }
    }

    private fetchJwtAndInitialize(): void {
        const userEid = this.authService.user?.externalId;

        Txt.initialize({ identifier: userEid });

        // if (!userEid) {
        //     return;
        // }

        // const qry = new GetUnlessTokenGQL(this.restService);
        // qry.fetch({}, { fetchPolicy: 'network-only' } as any).subscribe(
        //     (response) => {
        //         const jwt = response.data?.auth?.getTokenUsingGET;
        //         if (!jwt) {
        //             Txt.initialize({ identifier: userEid });
        //             return;
        //         }

        //         const data: TxtProfileData[] = [];
        //         if (this.authService.user?.email) {
        //             data.push({ key: 'email', value: this.authService.user.email, type: 'text' });
        //         }
        //         if (this.authService.user?.firstName) {
        //             data.push({ key: 'firstName', value: this.authService.user.firstName, type: 'text' });
        //         }
        //         if (this.authService.user?.lastName) {
        //             data.push({ key: 'lastName', value: this.authService.user.lastName, type: 'text' });
        //         }

        //         Txt.initialize({
        //             identifier: userEid,
        //             jwt: jwt,
        //             data: data
        //         });
        //     },
        //     () => {
        //         // Fallback: initialize without JWT
        //         Txt.initialize({ identifier: userEid });
        //     }
        // );
    }
}
